<template>
  <div>
    <div
      id="chartdiv"
      ref="chartdiv"
    />
    <div class="is-flex is-justify-content-center mb-5">
      <div style="max-width: 500px; width: 100%">
        <b-dropdown
          ref="multipleDropdown"
          aria-role="list"
          scrollable
          max-height="200"
          expanded
        >
          <template #trigger>
            <b-button
              type="is-secondary"
              icon-pack="fas"
              outlined
              class="btn-icon is-fullwidth is-flex is-justify-content-space-between"
              icon-right="fa-chevron-down"
            >{{ getDropdownLabel() }}
            </b-button>
          </template>
          <b-dropdown-item v-if="legends && legends.length" custom style="cursor: pointer" aria-role="listitem">
            <b-checkbox
              :value="selectedLegends.length === legends.length"
              :indeterminate="selectedLegends.length !== legends.length && selectedLegends.length !== 0"
              @input.native="selectAllLegends"
            >Выбрать все
            </b-checkbox>
          </b-dropdown-item>
          <b-dropdown-item
            v-for="(legend, idx) in legends || []"
            custom
            :key="legend.name"
            :value="legend.name"
            aria-role="listitem"
          >
            <b-checkbox
              v-model="selectedLegends"
              :native-value="legend"
              @input.native="onLegendCheck(legend.name, idx)"
            >
              <div class="is-flex is-align-items-center">
                <span
                  class="mr-1"
                  :style="{ backgroundColor: legend.color, width: '12px', height: '12px', borderRadius: '100%' }"
                ></span>
                {{ legend.name }}
              </div>
            </b-checkbox>
          </b-dropdown-item>
        </b-dropdown>
      </div>
    </div>

    <!-- Comment marks toggle -->
    <div class="is-flex is-justify-content-center mb-3">
      <b-checkbox v-model="showCommentMarks" @input="handleCommentMarkerChange">
        Показать комментарии
      </b-checkbox>
    </div>
  </div>
</template>

<script>
import * as am4core from "@amcharts/amcharts4/core"
import * as am4charts from "@amcharts/amcharts4/charts"
import am4lang_ru_RU from "@amcharts/amcharts4/lang/ru_RU"

export default {
  props: [
    "agents",
    "levels",
    "predictions",
    "predictionChannels",
    "graphData",
    "graphDataWithPredictions",
    "checkedPredictionNames",
    "temperatures",
    "warnings",
    "comments",
    "selectedTrends",
    "showTemperatureText",
  ],
  data() {
    return {
      device_id: null,
      chart: null,
      data: [],
      alarmSeries: null,
      warnSeries: null,
      button: "",
      legends: [],
      selectedLegends: [],
      ValueAxis: null,
      DateAxis: undefined,
      comment_ranges: [],
      seriesPredictMean: [],
      showCommentMarks: true,
      trendColor: ["#FAA700", "#6DD400", "#0091FF", "#6236FF", "#ff00c8", "#9B9B9B", "#44D7B6", "#0D7B80"],
      temperatureColorList: ["#1CD3A2", "#4C5866", "#8A2BE2", "#50C878", "#4169E1"],
    }
  },
  watch: {
    graphData(newValue) {
      this.chart.data = newValue
      this.chart.validate()
    },
    checkedPredictionNames: {
      immediate: false,
      handler(newValue, oldValue) {
        if (newValue !== oldValue) {
          for (let name in this.seriesPredictMean) {
            this.chart.series.removeIndex(this.chart.series.indexOf(this.seriesPredictMean[name])).dispose()
          }

          this.seriesPredictMean = []
          if (Array.isArray(newValue)) {
            this.chart.data = newValue.length > 0 ? this.graphDataWithPredictions : this.graphData
            this.chart.validateData()
            newValue.forEach((name) => {
              this.drawPredictions(name)
            })
          }
        }
      },
    },
    levels() {
      this.levels.includes("Alarm") ? this.alarmSeries.show() : this.alarmSeries.hide()
      this.levels.includes("Warning") ? this.warnSeries.show() : this.warnSeries.hide()
    },
    selectedLegends(newValue) {
      if (newValue) {
        this.$emit("legendSelected", newValue)
      }
    },
    // Watch for changes in comments prop
    comments: {
      immediate: false,
      handler(newValue) {
        if (this.chart && newValue) {
          this.createCommentMarks()
        }
      },
    },
  },
  mounted() {
    //create an instance of amCharts
    let chart = am4core.create(this.$refs.chartdiv, am4charts.XYChart)

    //language
    chart.language.locale = am4lang_ru_RU

    let data = this.graphData
    chart.data = data
    this.data = data

    // Create axes date X
    let dateAxis = chart.xAxes.push(new am4charts.DateAxis())
    dateAxis.renderer.grid.template.location = 0
    dateAxis.renderer.minGridDistance = 80
    dateAxis.startLocation = 0
    dateAxis.endLocation = 0
    dateAxis.keepSelection = true

    chart.events.on("datavalidated", () => {
      setTimeout(() => {
        const firstDate = new Date(this.graphData[0]?.time)
        const lastDate = new Date(this.graphData[this.graphData.length - 1]?.time)

        this.DateAxis.zoomToDates(firstDate, lastDate, false, true)

        setTimeout(() => {
          this.chart.yAxes.each(axis => {
            axis.invalidateData()
          })
          this.alarmSeries.invalidateData()
          this.warnSeries.invalidateData()
        }, 50)
      }, 100)
    })

    this.DateAxis = dateAxis

    let valueAxisTemperature
    if (this.showTemperatureText) {
      valueAxisTemperature = this.createValueAxis(chart, "C", "400")
    }
    const valueAxis = this.createValueAxis(chart, "мм/с")
    valueAxis.syncWithAxis = valueAxisTemperature

    const valueAxisSpeed = this.createValueAxis(chart, "об/мин", "600", true, 0)
    valueAxisSpeed.syncWithAxis = valueAxis

    this.ValueAxis = valueAxis

    this.agents.forEach((name, idx) => {
      if (name !== "SPEED")
        this.createSeries(chart, "avg" + (idx + 1), name, idx, valueAxis, this.trendColor[idx], false, false, "#.00")
    })

    this.temperatures && this.temperatures.forEach((temp, idx) => {
      this.createSeries(chart, "tmp" + (idx + 1), temp, idx + 10, valueAxisTemperature, this.temperatureColorList[idx] ?? null, false, false, "#.00")
    })

    this.createSeries(chart, "speed", "Скорость", 0, valueAxisSpeed, "#B620E0")

    this.alarmSeries = this.createSeries(chart, "alarm", "Тревога", 0, this.ValueAxis, "red")
    this.warnSeries = this.createSeries(chart, "warning", "Предупреждение", 0, this.ValueAxis, "red")

    chart.cursor = new am4charts.XYCursor()
    chart.cursor.behavior = "zoomX"
    chart.cursor.xAxis = dateAxis

    this.chart = chart

    chart.series.values?.forEach((lineSeries) => {
      if ( !lineSeries.hiddenInLegend) this.legends.push({
        name: lineSeries.name,
        color: lineSeries.stroke.hex,
      })
    })
    this.selectedLegends = this.selectedTrends?.length ? this.legends.filter(legend => this.selectedTrends.includes(legend.name)) : [...this.legends]

    if (this.comments?.length > 0) {
      this.createCommentMarks()
    }
  },
  beforeDestroy() {
    if (this.chart) {
      this.chart.dispose()
    }
  },
  methods: {
    getDropdownLabel() {
      if ( !this.selectedLegends) return "Выбрать"

      if (this.selectedLegends.length === 0) return "Выбрать"
      else return this.selectedLegends.map(leg => leg.name).join(", ")
    },
    handleCommentMarkerChange(show) {
      this.comment_ranges.forEach(commentData => {
        if (show) {
          commentData?.range?.show()
        } else {
          commentData?.range?.hide()
        }
      })
    },
    createCommentMarks() {
      // Clear existing comment ranges
      this.comment_ranges.forEach(commentData => {
        if (commentData.range) {
          this.DateAxis.axisRanges.removeValue(commentData.range)
        }
      })
      this.comment_ranges = []

      if ( !this.comments || !this.comments.length || !this.DateAxis) return

      // Create new comment marks
      this.comments.forEach((comment, index) => {
        const commentDate = new Date(comment.date)
        const range = this.createCommentRange(commentDate, comment.text, comment.title, "#666")

        this.comment_ranges.push({
          range: range,
          title: comment.title,
          text: comment.text,
          date: comment.date,
          id: comment.id,
        })
      })

      // Show/hide based on current state
      this.handleCommentMarkerChange(this.showCommentMarks)
    },
    createCommentRange(date, title, text, color = "#4A90E2") {
      if ( !this.DateAxis) return null

      const range = this.DateAxis.axisRanges.create()

      range.date = date
      range.grid.above = true
      range.grid.stroke = am4core.color(color)
      range.grid.strokeWidth = 1
      range.grid.strokeOpacity = 1

      range.label.inside = true
      range.label.text = text
      range.label.fill = range.grid.stroke
      range.label.rotation = -90
      range.label.verticalCenter = "middle"
      range.label.horizontalCenter = "right"
      range.label.fontSize = 12
      range.label.fontWeight = "600"
      range.label.paddingBottom = 24
      range.label.valign = "top"

      range.label.wrap = true
      range.label.maxWidth = 300
      range.label.truncate = true

      range.axisFill.tooltip = new am4core.Tooltip()
      range.axisFill.tooltipText = `[bold]${title}[/]\n${text}`
      range.axisFill.interactionsEnabled = true
      range.axisFill.isMeasured = true
      range.axisFill.fill = am4core.color(color)
      range.axisFill.fillOpacity = 0.1

      range.axisFill.events.on("over", function() {
        range.grid.strokeOpacity = 1
        range.axisFill.fillOpacity = 0.2
      })

      range.axisFill.events.on("out", function() {
        range.grid.strokeOpacity = 0.8
        range.axisFill.fillOpacity = 0.1
      })

      return range
    },
    createSeries(chart, field, name, zIndex, valueAxis, color, hiddenInLegend = false, connected = true, formatter = null) {
      if (chart) {
        const series = chart.series.push(new am4charts.LineSeries())
        series.dataFields.valueY = field
        series.dataFields.dateX = "time"
        series.name = name
        series.id = `${field}-${zIndex}`
        series.hiddenInLegend = hiddenInLegend
        series.sequencedInterpolation = hiddenInLegend
        series.connect = connected
        series.autoGapCount = 20

        if (color) {
          series.tooltip.fillcolor = color
          series.stroke = color
        }
        if (["alarm", "warning"].includes(field)) {
          series.strokeDasharray = field === "warning" ? "8,4" : "0"
          series.hiddenInLegend = true
          series.cursorTooltipEnabled = false
        }
        if (formatter) {
          series.numberFormatter = new am4core.NumberFormatter()
          series.numberFormatter.numberFormat = formatter
        }
        series.tooltipText = "[{stroke.hex}]●[/]{name}: [bold]{valueY}[/]"
        series.tooltip.getFillFromObject = false
        series.tooltip.background.fill = am4core.color("#fff")
        series.tooltip.label.fill = am4core.color("#000")
        series.strokeWidth = 2
        series.zIndex = zIndex
        series.yAxis = valueAxis
        series.chart = chart
        series.tensionX = 1

        return series
      }
    },
    toggleSlice(index) {
      let series = this.chart.series.values[index]

      if (series.isHiding || series.isHidden) {
        series.show()
      } else {
        series.hide()
      }
    },
    selectAllLegends() {
      if ( !this.legends) return
      const allLegendsSelected = this.legends.length === this.selectedLegends.length
      if ( !allLegendsSelected) {
        this.selectedLegends = [...this.legends]
      } else {
        this.selectedLegends = []
      }

      this.chart?.series.each((series) => {
        if (series.name === "Предупреждение" || series.name === "Тревога") return
        return allLegendsSelected ? series.hide() : series.show()
      })
    },
    createValueAxis(chart, text, fontWeight = "600", isOpposite = false, min) {
      if (chart) {
        let valueAxis = chart.yAxes.push(new am4charts.ValueAxis())
        valueAxis.renderer.opposite = isOpposite
        valueAxis.showOnInit = true
        valueAxis.title.text = text
        valueAxis.title.rotation = 90
        valueAxis.title.valign = "top"
        valueAxis.title.fontWeight = fontWeight
        valueAxis.renderer.grid.template.strokeOpacity = 0.1
        valueAxis.min = !isNaN(min) ? min : undefined

        return valueAxis
      }
      return null
    },
    onLegendCheck(legend, index) {
      this.toggleSlice(index)
    },
    drawPredictions(predictionChannelName) {
      this.predictionChannels.forEach((pred, i) => {
        if (pred === predictionChannelName) {
          this.seriesPredictMean[predictionChannelName] = this.chart.series.push(new am4charts.LineSeries())
          this.seriesPredictMean[predictionChannelName].dataFields.valueY = pred === "SPEED" ? "mean_speed" : "mean" + (i + 1)
          this.seriesPredictMean[predictionChannelName].dataFields.dateX = "time"
          this.seriesPredictMean[predictionChannelName].name = `prediction ${predictionChannelName}`
          this.seriesPredictMean[predictionChannelName].tooltipText = "{name}: [bold]{valueY}[/]"
          this.seriesPredictMean[predictionChannelName].strokeWidth = 3
          this.seriesPredictMean[predictionChannelName].stroke = "red"
          this.seriesPredictMean[predictionChannelName].zIndex = 0
          this.seriesPredictMean[predictionChannelName].hiddenInLegend = true
          this.seriesPredictMean[predictionChannelName].yAxis = this.ValueAxis
          this.seriesPredictMean[predictionChannelName].sequencedInterpolation = true
          this.seriesPredictMean[predictionChannelName].ignoreMinMax = true
        }
      })
    },
  },
}
</script>
